import { memo } from 'react';
import { css, type Theme } from '@emotion/react';
import { ConfigurationInput } from 'client/components/Input/ConfigurationInput';
import { Typography } from 'client/components/Typography';
import { ComponentIcon } from 'client/sections/components/ComponentIcon';
import { useComponent } from 'client/sections/components/componentUtils';
import { useProjectOrDeploymentState } from 'client/sections/sectionsUtils';

interface ImportedEnvironmentInputProps {
  parentStackComponentID: number;
  stackComponentID: number;
  identifier: string;
  value: string | number | boolean | null;
}

export const ImportedEnvironmentInput = memo(
  ({ parentStackComponentID, stackComponentID, identifier, value }: ImportedEnvironmentInputProps) => {
    const component = useComponent(stackComponentID);
    const isEnvOverridden = useProjectOrDeploymentState((state) =>
      state.document?.componentsWithValues
        .find((component) => component.stackComponentID === parentStackComponentID)
        ?.environmentVariables.some((env) => env.identifier === identifier)
    );

    /**
     * Enable changing textarea height based on content.
     */
    const handleRef = (element: HTMLInputElement | null) => {
      if (!element) return;
      element.parentElement?.parentElement?.setAttribute('style', `--height: ${element.scrollHeight}px`);
    };

    return (
      <div css={[inputGroupStyle, isEnvOverridden && inputGroupOverriddenStyle]}>
        {component && (
          <Typography variant="body2" component="label" css={labelStyle}>
            {component.name}
            <ComponentIcon size={18} url={component.packageUrl || ''} type="component" hideType />
          </Typography>
        )}
        <ConfigurationInput
          id={`env.${stackComponentID}.${identifier}.key`}
          label=""
          value={identifier}
          rows={1}
          canCopy
          multiline
          ref={handleRef}
          css={inputStyle}
        />
        <ConfigurationInput
          id={`env.${stackComponentID}.${identifier}.value`}
          label=""
          value={value}
          rows={1}
          canCopy
          multiline
          ref={handleRef}
          css={inputStyle}
        />
      </div>
    );
  }
);

const inputGroupStyle = (theme: Theme) => css`
  position: relative;
  display: flex;
  align-items: stretch;
  gap: 2px;

  fieldset {
    min-height: min(var(--height), 164px);
  }

  .input {
    display: flex;
    align-items: stretch;
    margin-bottom: 0 !important;
    min-height: min(var(--height), 164px);

    textarea {
      padding-right: 28px;
      border-radius: 0;
    }
  }

  &:first-of-type {
    > fieldset:first-of-type > .input textarea {
      border-radius: ${theme.borderRadius}px 0 0 0;
    }

    > fieldset:last-of-type > .input textarea {
      border-radius: 0 ${theme.borderRadius}px 0 0;
    }
  }

  &:last-of-type {
    > fieldset:first-of-type > .input textarea {
      border-radius: 0 0 0 ${theme.borderRadius}px;
    }

    > fieldset:last-of-type > .input textarea {
      border-radius: 0 0 ${theme.borderRadius}px 0;
    }
  }
`;

const inputGroupOverriddenStyle = (theme: Theme) => css`
  textarea {
    text-decoration: line-through;
    opacity: 0.5;
  }
`;

const inputStyle = css`
  label {
    display: none;
  }

  textarea {
    padding: 8px 12px;
  }

  > .icon {
    padding: 0 8px;

    > .body2 {
      display: none;
    }
  }
`;

const labelStyle = css`
  position: absolute;
  display: flex;
  align-items: center;
  gap: 5px;
  left: 0;
  padding-right: 10px;
  height: 100%;
  transform: translateX(-100%);
`;
